import { CheckCircle2, XCircle, FileText, AlertCircle } from "lucide-react";

// Pretty labels for clinical_form keys
const LABELS = {
  heard_from: "¿Cómo se enteró?",
  heard_from_other: "Especificación",
  marketing_optin: "Acepta marketing",
  takes_medication: "Toma medicamentos",
  medication_detail: "Medicamentos",
  has_allergies: "Tiene alergias",
  allergies_detail: "Alergias",
  recent_surgery: "Cirugía reciente",
  surgery_detail: "Detalle cirugía",
  pregnant: "Embarazo",
  birth_control: "Anticonceptivos",
  hormone_therapy: "Terapia hormonal",
  conditions: "Afecciones",
  conditions_other: "Otra afección",
  skin_concerns: "Preocupaciones de piel",
  allergy_ingredients: "Alergias a componentes",
  allergy_ingredients_other: "Otra alergia",
  skin_type: "Tipo de piel",
  skin_state: "Estado actual",
  routine_makeup_remover: "Desmaquillante",
  routine_cleanser: "Limpiador",
  routine_serum: "Suero",
  routine_exfoliant: "Exfoliante",
  routine_sunscreen: "Protector solar",
  routine_other: "Otro producto",
  smokes_drinks: "Fuma o consume alcohol",
  has_injections: "Inyecciones previas",
  injections_detail: "Detalle inyecciones",
  recent_treatments: "Tratamientos recientes",
  expectations: "Expectativas",
  consent: "Consentimiento firmado",
};

const SECTIONS = [
  {
    title: "Cómo nos conoció",
    keys: ["heard_from", "heard_from_other", "marketing_optin"],
  },
  {
    title: "Historial médico",
    keys: [
      "takes_medication", "medication_detail",
      "has_allergies", "allergies_detail",
      "recent_surgery", "surgery_detail",
      "pregnant", "birth_control", "hormone_therapy",
    ],
  },
  {
    title: "Afecciones",
    keys: ["conditions", "conditions_other"],
  },
  {
    title: "Preocupaciones de piel",
    keys: ["skin_concerns"],
  },
  {
    title: "Alergias a componentes",
    keys: ["allergy_ingredients", "allergy_ingredients_other"],
  },
  {
    title: "Tipo y estado de piel",
    keys: ["skin_type", "skin_state"],
  },
  {
    title: "Rutina de cuidado",
    keys: ["routine_makeup_remover", "routine_cleanser", "routine_serum", "routine_exfoliant", "routine_sunscreen", "routine_other"],
  },
  {
    title: "Hábitos y tratamientos previos",
    keys: ["smokes_drinks", "has_injections", "injections_detail", "recent_treatments"],
  },
  {
    title: "Expectativas",
    keys: ["expectations"],
  },
];

const MEDICAL_FLAG_KEYS = [
  "takes_medication", "has_allergies", "recent_surgery", "pregnant",
];

function renderValue(key, val) {
  if (val === null || val === undefined || val === "") return null;
  if (typeof val === "boolean") {
    return val ? (
      <span className="inline-flex items-center gap-1 text-[#367B5A] text-sm"><CheckCircle2 className="w-4 h-4" /> Sí</span>
    ) : (
      <span className="inline-flex items-center gap-1 text-[#6F5A4C] text-sm"><XCircle className="w-4 h-4" /> No</span>
    );
  }
  if (val === "si") return <span className="inline-flex items-center gap-1 text-[#B5704A] font-medium text-sm"><CheckCircle2 className="w-4 h-4" /> Sí</span>;
  if (val === "no") return <span className="inline-flex items-center gap-1 text-[#6F5A4C] text-sm"><XCircle className="w-4 h-4" /> No</span>;
  if (Array.isArray(val)) {
    if (val.length === 0) return null;
    return (
      <div className="flex flex-wrap gap-1.5 mt-1">
        {val.map((v) => (
          <span key={v} className="inline-flex items-center px-2 py-1 bg-[#B5704A]/10 text-[#B5704A] rounded-full text-xs font-medium">
            {v}
          </span>
        ))}
      </div>
    );
  }
  return <span className="text-[#3A2418] text-sm">{String(val)}</span>;
}

export default function ClinicalFormView({ patient }) {
  const cf = patient?.clinical_form;

  if (!cf || Object.keys(cf).length === 0) {
    return (
      <div className="surface-card p-12 text-center">
        <FileText className="w-12 h-12 mx-auto mb-4 text-[#6F5A4C] opacity-40" />
        <h3 className="font-display text-xl text-[#3A2418]">Sin ficha clínica</h3>
        <p className="text-sm text-[#6F5A4C] mt-2">Esta clienta aún no ha completado su ficha de admisión.</p>
        <p className="text-xs text-[#6F5A4C] mt-1 italic">Cuando reserve online a través de la web, su ficha aparecerá aquí.</p>
      </div>
    );
  }

  // Compute medical alerts
  const alerts = [];
  if (cf.has_allergies === "si") alerts.push(`Alergias: ${cf.allergies_detail || "sin detalle"}`);
  if (cf.takes_medication === "si") alerts.push(`Medicamentos: ${cf.medication_detail || "sin detalle"}`);
  if (cf.recent_surgery === "si") alerts.push(`Cirugía reciente: ${cf.surgery_detail || "sin detalle"}`);
  if (cf.pregnant === "si") alerts.push("⚠️ Embarazada");
  if (Array.isArray(cf.allergy_ingredients) && cf.allergy_ingredients.length > 0) {
    alerts.push(`Alergias a: ${cf.allergy_ingredients.join(", ")}`);
  }

  return (
    <div className="space-y-4">
      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="surface-card p-5 border-l-4 border-l-[#D87D65] bg-[#FDF3EF]" data-testid="clinical-alerts">
          <div className="flex items-center gap-2 mb-2">
            <AlertCircle className="w-5 h-5 text-[#D87D65]" />
            <h3 className="font-display text-lg font-[600] text-[#3A2418]">Información médica importante</h3>
          </div>
          <ul className="text-sm text-[#3A2418] space-y-1 ml-7">
            {alerts.map((a) => <li key={a} className="list-disc">{a}</li>)}
          </ul>
        </div>
      )}

      {/* Consent indicator */}
      {cf.consent && (
        <div className="surface-card p-4 flex items-center gap-3">
          <CheckCircle2 className="w-5 h-5 text-[#367B5A]" />
          <span className="text-sm text-[#3A2418]">Declaración de consentimiento aceptada</span>
          {patient.clinical_form_updated_at && (
            <span className="text-xs text-[#6F5A4C] ml-auto">
              {new Date(patient.clinical_form_updated_at).toLocaleString("es", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
        </div>
      )}

      {/* Sections */}
      {SECTIONS.map((section) => {
        const items = section.keys
          .map((k) => ({ key: k, val: cf[k] }))
          .filter(({ val }) => val !== undefined && val !== null && val !== "" && !(Array.isArray(val) && val.length === 0));
        if (items.length === 0) return null;
        return (
          <div key={section.title} className="surface-card p-6" data-testid={`section-${section.title}`}>
            <h3 className="font-display text-xl font-[600] text-[#3A2418] mb-4 pb-2 border-b border-[#E8DFD2]">{section.title}</h3>
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
              {items.map(({ key, val }) => (
                <div key={key} className={Array.isArray(val) ? "sm:col-span-2" : ""}>
                  <dt className="label-eyebrow text-[#6F5A4C] text-[10px]">{LABELS[key] || key}</dt>
                  <dd className="mt-1">{renderValue(key, val)}</dd>
                </div>
              ))}
            </dl>
          </div>
        );
      })}
    </div>
  );
}
